/**
 * Wizard Step 3 — Roles concept intro.
 *
 * Pure educational screen (no input). Explains the distinction between
 * a keyholder (limited access, multiple allowed) and a successor (full
 * access, one only). Wording matches the authoritative copy in
 * ProfileScreen.tsx so users don't hit a contradiction when they later
 * set up these roles for real. Primes the user for the successor
 * soft-ask on the final wizard step.
 */
import React, { useEffect } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { WizardShell } from '../components/WizardShell';
import { ms } from '../../../utils/responsive';
import { analytics } from '../../../services/analytics/analytics';
import { AnalyticsEvent } from '../../../services/analytics/events';

const WIZARD_STEP = 3;
const TOTAL_STEPS = 6;

export const WizardRolesIntroScreen: React.FC = () => {
  const navigation = useNavigation<any>();

  useEffect(() => {
    analytics.track(AnalyticsEvent.WizardStepShown, { step: 'roles_intro', number: WIZARD_STEP });
  }, []);

  const handleContinue = () => {
    analytics.track(AnalyticsEvent.WizardStepCompleted, { step: 'roles_intro' });
    navigation.navigate('WizardProfessionals');
  };

  return (
    <WizardShell
      stepNumber={WIZARD_STEP}
      totalSteps={TOTAL_STEPS}
      title="Who will unlock this for your loved ones?"
      subtitle="Two roles matter in FinalKey. You'll pick specific people later — here's what each one does."
      primaryLabel="Got it"
      onPrimaryPress={handleContinue}
      onBackPress={() => navigation.goBack()}
    >
      <View style={styles.card}>
        <Text style={styles.roleTitle}>🎯 Successor</Text>
        <Text style={styles.roleBody}>
          Your executor. The <Text style={styles.bold}>one person</Text> who gets full access to
          everything you've stored and carries out your wishes. Most people pick their spouse,
          an adult child, or their attorney.
        </Text>
      </View>

      <View style={styles.card}>
        <Text style={styles.roleTitle}>🔑 Keyholder</Text>
        <Text style={styles.roleBody}>
          A trusted person you give <Text style={styles.bold}>limited access</Text> to. You pick
          exactly what they can see — maybe just your final wishes or your list of contacts.
          You can have as many keyholders as you want.
        </Text>
      </View>

      <View style={styles.noteCard}>
        <Text style={styles.noteText}>
          At the end of this setup we'll help you name your successor. Keyholders you can add
          later from your profile, whenever you're ready.
        </Text>
      </View>
    </WizardShell>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(16),
    padding: ms(18),
    marginBottom: ms(14),
    borderWidth: ms(1),
    borderColor: '#E1E5EA',
  },
  roleTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  roleBody: {
    fontSize: ms(15, 0.3),
    color: '#4B5563',
    lineHeight: ms(22, 0.3),
  },
  bold: {
    fontWeight: '700',
    color: '#1C2C4F',
  },
  noteCard: {
    backgroundColor: '#F0FBFB',
    borderRadius: ms(12),
    padding: ms(14),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    marginTop: ms(6),
  },
  noteText: {
    fontSize: ms(14, 0.3),
    color: '#0F4F50',
    lineHeight: ms(20, 0.3),
  },
});
